export const STARTED_PROGRESS = 5;
export const MAX_IN_PROGRESS = 99;

const COMPLETION_STATUSES = new Set(['completed', 'passed']);
const COMPLETION_ELEMENTS = new Set([
  'cmi.core.lesson_status',
  'cmi.completion_status',
  'cmi.success_status',
]);
const SCORE_ELEMENTS = new Set([
  'cmi.core.score.raw',
  'cmi.score.raw',
  'cmi.score.scaled',
]);
const MEASURE_ELEMENTS = new Set(['cmi.progress_measure']);
const LOCATION_ELEMENTS = new Set([
  'cmi.core.lesson_location',
  'cmi.location',
]);

const COMPLETION_URL_PATTERNS = [
  'goodbye.html',
  'goodby.html',
  'completion.html',
  'finished.html',
  'complete.html',
  'final.html',
  'end.html',
];

export interface ScormRuntimeProgress {
  lessonStatus?: string;
  score?: number;
  location?: string;
  suspendData?: string;
  totalTime?: string;
  sessionTime?: string;
  interactions?: unknown[];
}

export function mergeProgress(current: number, next: number): number {
  const safeCurrent = Number.isFinite(current) ? current : 0;
  const safeNext = Number.isFinite(next) ? next : 0;
  return Math.max(0, Math.min(100, Math.max(safeCurrent, safeNext)));
}

export function isCompletionStatus(element: string, value: string): boolean {
  if (!COMPLETION_ELEMENTS.has(element)) return false;
  return COMPLETION_STATUSES.has(String(value).trim().toLowerCase());
}

export function isCompletionUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  const normalized = url.toLowerCase();
  return COMPLETION_URL_PATTERNS.some((pattern) => normalized.includes(pattern));
}

export function progressFromScore(value: string): number | null {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  if (parsed <= 1) {
    return Math.min(MAX_IN_PROGRESS, Math.round(parsed * 100));
  }
  return Math.min(MAX_IN_PROGRESS, Math.round(parsed));
}

export function progressFromMeasure(value: string): number | null {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  const percent = parsed <= 1 ? parsed * 100 : parsed;
  return Math.min(MAX_IN_PROGRESS, Math.round(percent));
}

export function progressFromLocationCount(uniqueLocationCount: number): number {
  if (uniqueLocationCount <= 0) return 0;
  return Math.min(MAX_IN_PROGRESS, STARTED_PROGRESS + uniqueLocationCount * STARTED_PROGRESS);
}

export function extractSavedScormProgress(payload: unknown): ScormRuntimeProgress | null {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const nested = record.scormData;
  const source =
    nested && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : record;

  const lessonStatus = typeof source.lessonStatus === 'string' ? source.lessonStatus : undefined;
  const location = typeof source.location === 'string' ? source.location : undefined;
  const suspendData = typeof source.suspendData === 'string' ? source.suspendData : undefined;
  const score = typeof source.score === 'number' ? source.score : undefined;

  if (
    lessonStatus === undefined &&
    location === undefined &&
    suspendData === undefined &&
    score === undefined &&
    !Array.isArray(source.interactions)
  ) {
    return null;
  }

  return {
    lessonStatus: lessonStatus || 'not attempted',
    score: score ?? 0,
    location: location || '',
    suspendData: suspendData || '',
    totalTime: typeof source.totalTime === 'string' ? source.totalTime : '0000:00:00',
    sessionTime: typeof source.sessionTime === 'string' ? source.sessionTime : '0000:00:00',
    interactions: Array.isArray(source.interactions) ? source.interactions : [],
  };
}

export class ScormProgressTracker {
  private current = 0;
  private readonly locations = new Set<string>();

  constructor(initialProgress = 0) {
    this.current = mergeProgress(0, initialProgress);
  }

  get progress(): number {
    return this.current;
  }

  markStarted(): number {
    return this.apply(STARTED_PROGRESS);
  }

  applyValue(element: string, rawValue: string): { progress: number; completed: boolean } {
    const value = String(rawValue);
    if (isCompletionStatus(element, value)) {
      return { progress: this.apply(100), completed: true };
    }

    if (SCORE_ELEMENTS.has(element)) {
      const fromScore = progressFromScore(value);
      if (fromScore !== null) this.apply(fromScore);
    }

    if (MEASURE_ELEMENTS.has(element)) {
      const fromMeasure = progressFromMeasure(value);
      if (fromMeasure !== null) this.apply(fromMeasure);
    }

    if (LOCATION_ELEMENTS.has(element) && value.trim()) {
      this.locations.add(value.trim());
      this.apply(progressFromLocationCount(this.locations.size));
    }

    return { progress: this.current, completed: this.current >= 100 };
  }

  applyFinish(status: string): { progress: number; completed: boolean } {
    if (COMPLETION_STATUSES.has(String(status).trim().toLowerCase())) {
      return { progress: this.apply(100), completed: true };
    }
    return { progress: this.current, completed: this.current >= 100 };
  }

  private apply(next: number): number {
    this.current = mergeProgress(this.current, next);
    return this.current;
  }
}
